import type { BandStats } from './types';
import { clamp } from './util';

export const SENTINELS = [-9999, -32768, -32767, -3.4028234663852886e38, 32767, 65535];

/**
 * @param valid optional per-pixel validity, from an internal mask. A masked file
 * declares no nodata value, so without this the zeroed pixels under the mask drag
 * every percentile toward zero.
 */
export function bandStats(
  arr: ArrayLike<number>, nodata: number | null, valid?: Uint8Array | null,
): BandStats {
  const vals: number[] = [];
  let sum = 0, total = 0, skipped = 0;
  for (let i = 0; i < arr.length; i++) {
    const x = arr[i] as number;
    total++;
    if (valid && !valid[i]) { skipped++; continue; }
    if (!Number.isFinite(x)) { skipped++; continue; }
    if (nodata != null && Math.abs(x - nodata) <= Math.max(1e-6, Math.abs(nodata) * 1e-9)) {
      skipped++; continue;
    }
    vals.push(x); sum += x;
  }
  if (!vals.length) {
    return { min: 0, max: 0, p2: 0, p50: 0, p98: 0, mean: 0, validFraction: 0,
      hist: new Array(48).fill(0), empty: true };
  }
  vals.sort((a, b) => a - b);
  const q = (p: number) => vals[clamp(Math.round(p * (vals.length - 1)), 0, vals.length - 1)] as number;
  const p2 = q(0.02), p98 = q(0.98);
  const lo = p2, hi = p98 > p2 ? p98 : p2 + 1;
  const hist = new Array(48).fill(0);
  for (const v of vals) hist[clamp(Math.floor((v - lo) / (hi - lo) * 48), 0, 47)]++;
  return {
    min: vals[0] as number, max: vals[vals.length - 1] as number,
    p2, p50: q(0.5), p98, mean: sum / vals.length,
    validFraction: (total - skipped) / total,
    hist, histLo: lo, histHi: hi, empty: false,
  };
}

/** An undeclared sentinel poisons every percentile, so catch it explicitly. */
export function detectSentinel(stats: BandStats, nodata: number | null): number | null {
  if (nodata != null) return null;
  for (const s of SENTINELS) {
    if (Math.abs(stats.min - s) < 1e-3 || Math.abs(stats.max - s) < 1e-3) {
      // A genuinely saturated image is not a sentinel.
      if ((s === 65535 || s === 32767) && stats.p98 >= s * 0.98) continue;
      return s;
    }
  }
  return null;
}
